import { Dialog } from '@mui/material';
import { Close } from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import classnames from 'classnames';
import { Suspense, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../../Button/Button';
import { Loader } from '../../Loader';

import styles from './Modal.module.scss';
import { ModalContext, modalContext, ModalProps } from './Modal.types';

export const Modal = ({
  children,
  onClose,
  disableBackdropClick,
  disableEscapeKey,
  disableCloseButton,
  size,
  className,
  ...props
}: ModalProps) => {
  const leftActionsRef = useRef<HTMLDivElement>(null);
  const rightActionsRef = useRef<HTMLDivElement>(null);
  const [leftActionsContainer, setLeftActionsContainer] = useState<HTMLDivElement>();
  const [rightActionsContainer, setRightActionsContainer] = useState<HTMLDivElement>();

  const [blockerCallback, setBlockerCallback] = useState<(blocked: boolean) => void>();
  const [blocked, setBlocked] = useState(false);
  const proceed = useCallback(() => {
    blockerCallback?.(false);
    onClose?.();
  }, [blockerCallback, onClose]);

  const contextValue = useMemo(
    (): ModalContext => ({
      leftActionsContainer,
      rightActionsContainer,
      blocked,
      setBlocked,
      proceed,
      setBlockerCallback,
    }),
    [leftActionsContainer, rightActionsContainer, blocked, proceed]
  );

  const close = useCallback(() => {
    if (blocked && blockerCallback) blockerCallback(true);
    else onClose?.();
  }, [blocked, blockerCallback, onClose]);

  useLayoutEffect(() => {
    setTimeout(() => {
      setLeftActionsContainer(leftActionsRef.current ?? undefined);
      setRightActionsContainer(rightActionsRef.current ?? undefined);
    });
  }, [props.open]);

  return (
    <Dialog
      onClose={(_, reason) => {
        if (reason === 'backdropClick' && disableBackdropClick) return;
        if (reason === 'escapeKeyDown' && disableEscapeKey) return;
        close();
      }}
      className={classnames(styles.modal, styles[size])}
      PaperProps={{ className }}
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      onKeyDown={event => event.stopPropagation()}
      onKeyUp={event => event.stopPropagation()}
      scroll="body"
      {...props}
    >
      <Suspense fallback={<Loader size="medium" />}>
        <modalContext.Provider value={contextValue}>
          {children}
          <div className={styles.actionsContainer}>
            <div className={styles.actions} ref={leftActionsRef} />
            <div className={styles.actions} ref={rightActionsRef} />
          </div>
          {!disableCloseButton && (
            <Button variant="tertiary" size="small" className={styles.closeButton} onClick={close}>
              <Close />
            </Button>
          )}
        </modalContext.Provider>
      </Suspense>
    </Dialog>
  );
};
